import { z } from 'zod';

import {
  savedDocumentAttachment,
  zodArrayOptional,
  zodArrayRequired,
  zodDate,
  zodPlaceholder,
  zodStringOnlyUkrainian,
  zodStringOptional,
  zodStringRequired,
  zodStringRequiredWithMinMax,
} from '@/shared/lib/validations';

import { DocumentTypeCode } from '@/api/types';

export const documentSchema = z.object({
  type: zodPlaceholder,
  info: zodPlaceholder,
  attachments: zodPlaceholder,
});

export const certIDPInfoSchema = z.object({
  serialNumberDoc: zodStringRequired,
  date: zodDate(),
});

export const baseOptionalFieldsSchema = z.object({
  serialNumberDoc: zodStringOptional,
  date: zodDate().nullish(),
});

export const certMarriageInfoOptionalSchema = z
  .object({
    auth_dep: zodStringOptional,
  })
  .merge(baseOptionalFieldsSchema);

export const extractPrprOptionalSchema = z
  .object({
    indexNumber: zodStringOptional,
  })
  .merge(certMarriageInfoOptionalSchema);

export const ownershipCertificateInfoOptionalSchema = z
  .object({
    indexNumber: zodStringOptional,
    auth_dep: zodStringOptional,
  })
  .merge(baseOptionalFieldsSchema);

export const insurancePolicySchema = z
  .object({
    expirationDate: zodDate(),
  })
  .merge(baseOptionalFieldsSchema);

export const medicalReferenceOptionalSchema = z
  .object({
    expirationDate: zodDate(),
  })
  .merge(certMarriageInfoOptionalSchema);

export const expertResearchInfoSchema = z.object({
  date: zodDate(),
  auth_dep: zodStringRequired,
});

export const powerOfAttorneyInfoSchema = z
  .object({
    expirationDate: zodDate(),
  })
  .merge(certIDPInfoSchema);

export const leaseAgreementInfoSchema = z.object({
  indexNumber: zodStringOptional,
  serialNumberDoc: zodStringOptional,
  date: zodDate().nullish(),
  expirationDate: zodDate().nullish(),
});

export const certMarriageInfoSchema = z
  .object({
    auth_dep: zodStringRequired,
  })
  .merge(certIDPInfoSchema);

export const ownershipCertificateInfoSchema = z
  .object({
    indexNumber: zodStringRequired,
  })
  .merge(certMarriageInfoSchema);

export const legalResidenceInfoSchema = z
  .object({
    expirationDate: zodDate().nullish(),
  })
  .merge(certMarriageInfoSchema);

export const permitInfoSchema = z.object({
  serialNumberDoc: zodStringRequired,
  date: zodDate(),
  expirationDate: zodDate().nullish(),
  auth_dep: zodStringRequired,
});

export const passportInfoSchema = z.object({
  serialNumberDoc: zodStringRequiredWithMinMax({ min: 8, max: 8 }),
  dateIssue: zodDate(),
  issue: zodStringOnlyUkrainian,
});

export const internationalPassportInfoSchema = z.object({
  serialNumberDoc: zodStringRequiredWithMinMax({ min: 8, max: 8 }),
  date: zodDate(),
  expirationDate: zodDate(),
  auth_dep: zodStringRequiredWithMinMax({ min: 4, max: 4 }),
  unzr: zodStringRequiredWithMinMax({ min: 14, max: 14 }),
});

export const idCardInfoSchema = z.object({
  serialNumberDoc: zodStringRequiredWithMinMax({ min: 9, max: 9 }),
  date: zodDate(),
  expirationDate: zodDate(),
  auth_dep: zodStringRequiredWithMinMax({ min: 4, max: 4 }),
  unzr: zodStringRequiredWithMinMax({ min: 14, max: 14 }),
});

export const oldResidenceInfoSchema = z.object({
  serialNumberDoc: zodStringRequiredWithMinMax({ min: 8, max: 8 }),
  date: zodDate(),
  auth_dep: zodStringOnlyUkrainian,
});

export const vityagInfoSchema = z.object({
  serialNumberDoc: zodStringRequired,
  date: zodDate(),
  auth_dep: zodStringRequired,
});

export const applicationSchema = z.object({
  date: zodDate(),
});

export const certificationInfoSchema = z.object({
  org: zodStringRequired,
  serialNumberDoc: zodStringRequired,
  auth_dep: zodStringRequired,
  date: zodDate(),
  expirationDate: zodDate().nullish(),
});

export const workReferenceInfoSchema = z.object({
  serialNumberDoc: zodStringRequired,
  auth_dep: zodStringRequired,
  date: zodDate(),
  expirationDate: zodDate().nullish(),
});

const emptyInfoSchema = z.object({}).passthrough();

const infoSchemasMap = {
  [DocumentTypeCode.LEASE_AGREEMENT]: leaseAgreementInfoSchema,
  [DocumentTypeCode.EXRACT_PRPR]: extractPrprOptionalSchema,
  [DocumentTypeCode.OWNERSHIP_CERTIFICATE]: ownershipCertificateInfoOptionalSchema,
  [DocumentTypeCode.PERMIT]: permitInfoSchema,
  [DocumentTypeCode.PASSPORT]: passportInfoSchema,
  [DocumentTypeCode.VITYAG_TERITORIALNOYI_GROMADI]: vityagInfoSchema,
  [DocumentTypeCode.CERT_MARRIAGE]: certMarriageInfoOptionalSchema,
  [DocumentTypeCode.TECHNICAL_CONDITION]: certMarriageInfoSchema,
  [DocumentTypeCode.LEGAL_RESIDENCE]: legalResidenceInfoSchema,
  [DocumentTypeCode.OLD_RESIDENCE]: oldResidenceInfoSchema,
  [DocumentTypeCode.MEDICAL_REFERENCE]: medicalReferenceOptionalSchema,
  [DocumentTypeCode.CERT_IDP]: certIDPInfoSchema,
  [DocumentTypeCode.POWER_OF_ATTORNEY]: powerOfAttorneyInfoSchema,
  [DocumentTypeCode.INSURANCE_POLICY]: insurancePolicySchema,
  [DocumentTypeCode.CONTRACT_SECURITY_ACTIVITIES]: powerOfAttorneyInfoSchema,
  [DocumentTypeCode.EXPERT_RESEARCH]: expertResearchInfoSchema,
  [DocumentTypeCode.INTERNATIONAL_PASSPORT]: internationalPassportInfoSchema,
  [DocumentTypeCode.ID_CARD]: idCardInfoSchema,
  [DocumentTypeCode.TEMPORARY_RESIDENCE]: idCardInfoSchema,
  [DocumentTypeCode.RESIDENCE]: idCardInfoSchema,
  [DocumentTypeCode.DISAPPEARANCE_MISSING_PERSON]: emptyInfoSchema,
  [DocumentTypeCode.PAYMENT_REFERENCE]: emptyInfoSchema,
  [DocumentTypeCode.WORK_REFERENCE]: workReferenceInfoSchema,
  [DocumentTypeCode.TECHNICAL_REFERENCE]: emptyInfoSchema,
  [DocumentTypeCode.INVITE]: emptyInfoSchema,
  [DocumentTypeCode.INVITE_UKR]: emptyInfoSchema,
  [DocumentTypeCode.RESOLUTION]: emptyInfoSchema,
  [DocumentTypeCode.CERTIFICATION]: certificationInfoSchema,
  [DocumentTypeCode.RNOKPP]: emptyInfoSchema,
  [DocumentTypeCode.PHOTO_CARD]: emptyInfoSchema,
  [DocumentTypeCode.MISSING_PERSON_PHOTO_CARD]: emptyInfoSchema,
  [DocumentTypeCode.MISSIN_PERSON_ADDITIONAL_PERSONAL_INFO]: emptyInfoSchema,
  [DocumentTypeCode.ADDITIONAL_DOCUMENT]: emptyInfoSchema,
  [DocumentTypeCode.EXTRACT_ERDR]: emptyInfoSchema,
  [DocumentTypeCode.ANCESTRAL_CONNECTION]: emptyInfoSchema,
  [DocumentTypeCode.WEAPON_LEFT_PHOTO]: emptyInfoSchema,
  [DocumentTypeCode.WEAPON_RIGHT_PHOTO]: emptyInfoSchema,
  [DocumentTypeCode.COMMISSION_SALE]: emptyInfoSchema,
  [DocumentTypeCode.PURCHASE]: emptyInfoSchema,
  [DocumentTypeCode.TRANSPORT_THROUGH_UKRAINE]: emptyInfoSchema,
  [DocumentTypeCode.IMPORT_IN_UKRAINE]: emptyInfoSchema,
  [DocumentTypeCode.TRANSPORT_ACROSS_BORDER]: emptyInfoSchema,
  [DocumentTypeCode.COLD_STEEL_TRANSPORTATION]: emptyInfoSchema,
  [DocumentTypeCode.STORAGE_CARRYING]: emptyInfoSchema,
  [DocumentTypeCode.COLD_STEEL_STORAGE_FOR_COLLECTION]: emptyInfoSchema,
  [DocumentTypeCode.RESIDENCE_RELOCATION]: emptyInfoSchema,
  [DocumentTypeCode.TEMPORARY_RESIDENCE_RELOCATION]: emptyInfoSchema,
  [DocumentTypeCode.VALIDITY_PERIOD_PROLONGATION]: emptyInfoSchema,
  [DocumentTypeCode.MISSING_PERSON]: emptyInfoSchema,
  [DocumentTypeCode.MISSING_PERSON_EXTRACT]: emptyInfoSchema,
  [DocumentTypeCode.LEARNING_DOCUMENT]: emptyInfoSchema,
  [DocumentTypeCode.OTHER]: emptyInfoSchema,
  [DocumentTypeCode.APPLICATION]: applicationSchema,
  [DocumentTypeCode.WEAPON_TECHNICAL_REFERENCE]: emptyInfoSchema,
  [DocumentTypeCode.CRIMINAL_RECORD]: emptyInfoSchema,
};

export const documentDataSchema = zodArrayRequired(documentSchema).superRefine((data, ctx) => {
  data.forEach((document, index) => {
    if (!document.type?.code) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Необхідно заповнити поле',
        path: [index, 'type'],
      });
      return;
    }

    const { code } = document.type;
    const schema = infoSchemasMap[code as keyof typeof infoSchemasMap];

    if (!schema) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing validation schema for document type: ${code}`,
        path: [index, 'type'],
      });
      return;
    }

    const { success: infoSuccess, error: infoError } = schema.safeParse(document.info);

    if (!infoSuccess) {
      for (const issue of infoError.issues) {
        ctx.addIssue({ ...issue, path: [index, 'info', ...issue.path] });
      }
    }

    const { success: attachmentsSuccess, error: attachmentsError } = savedDocumentAttachment.safeParse(
      document.attachments,
    );

    if (!attachmentsSuccess) {
      for (const issue of attachmentsError.issues) {
        ctx.addIssue({ ...issue, path: [index, 'attachments', ...issue.path] });
      }
    }
  });
});

export const documentDataSchemaOptional = zodArrayOptional(documentSchema).superRefine((data, ctx) => {
  data.forEach((document, index) => {
    if (!document.type?.code) {
      return;
    }

    const { code } = document.type;
    const schema = infoSchemasMap[code as keyof typeof infoSchemasMap];

    if (!schema) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Missing validation schema for document type: ${code}`,
        path: [index, 'type'],
      });
      return;
    }

    const { success: infoSuccess, error: infoError } = schema.safeParse(document.info);

    if (!infoSuccess) {
      for (const issue of infoError.issues) {
        ctx.addIssue({ ...issue, path: [index, 'info', ...issue.path] });
      }
    }

    const { success: attachmentsSuccess, error: attachmentsError } = savedDocumentAttachment.safeParse(
      document.attachments,
    );

    if (!attachmentsSuccess) {
      for (const issue of attachmentsError.issues) {
        ctx.addIssue({ ...issue, path: [index, 'attachments', ...issue.path] });
      }
    }
  });
});
